import { useDataPart } from '@ai-sdk-tools/store';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { UIMessageWithMetadata } from '../../../lib/types';
import { type CoTStep, type ReasoningState, type ToolInvocationState, getCoTStepStatus, getField, TOOL_LABELS } from '../lib/chain-of-thought-utils';
interface ChainOfThoughtMeta {
  steps: CoTStep[];
  hasTextPart: boolean;
  lastTitle: string | undefined;
}
interface ChainOfThoughtStepsResult {
  chainOfThoughtMeta: ChainOfThoughtMeta;
  reasoningIsStreaming: boolean;
  stepProgressMap: Record<string, string>;
}
const EMPTY_META: ChainOfThoughtMeta = {
  steps: [],
  hasTextPart: false,
  lastTitle: undefined
};
const EMPTY_PROGRESS: Record<string, string> = {};
export function useChainOfThoughtSteps(message: UIMessageWithMetadata, isStreaming: boolean, isLast: boolean, enabled: boolean): ChainOfThoughtStepsResult {
  const [progressData] = useDataPart<{
    message?: string;
  }>('progress');
  const chainOfThoughtMeta = useMemo(() => {
    if (!enabled) return EMPTY_META;
    const {
      parts
    } = message;
    if (!parts) return EMPTY_META;
    const steps = parts.map((part): CoTStep | null => {
      if (part.type === 'reasoning') {
        if (part.text) {
          const title = part.text.match(/^\*\*(.+?)\*\*/)?.[1];
          return {
            type: 'reasoning',
            text: part.text,
            title,
            state: part.state as ReasoningState
          };
        }
        if (part.state === 'streaming') {
          return {
            type: 'reasoning',
            text: '',
            title: 'Thinking...',
            state: 'streaming'
          };
        }
      } else if (part.type.startsWith('tool-')) {
        const toolName = part.type.replace('tool-', '');
        const label = TOOL_LABELS[toolName] ?? `Running ${toolName}...`;
        const toolState = getField<ToolInvocationState>(part, 'state');
        const toolInput = getField<unknown>(part, 'input');
        const toolOutput = toolState === 'output-available' ? getField<unknown>(part, 'output') : undefined;
        return {
          type: 'tool',
          toolName,
          label,
          state: toolState,
          input: toolInput,
          output: toolOutput
        };
      }
      return null;
    }).filter((step): step is CoTStep => step !== null);

    // Reasoning steps without their own title inherit from the closest preceding reasoning title
    const stepsWithInheritedTitles = steps.map((step_0, i) => {
      if (step_0.type !== 'reasoning' || step_0.title) return step_0;
      const preceding = steps.slice(0, i).reverse();
      const previousTitle = preceding.find((s): s is CoTStep & {
        type: 'reasoning';
        title: string;
      } => s.type === 'reasoning' && !!s.title);
      return previousTitle ? {
        ...step_0,
        title: previousTitle.title
      } : step_0;
    });
    const lastReasoningTitle = [...stepsWithInheritedTitles].reverse().find((s_0): s_0 is CoTStep & {
      type: 'reasoning';
      title: string;
    } => s_0.type === 'reasoning' && !!s_0.title)?.title;
    return {
      steps: stepsWithInheritedTitles,
      hasTextPart: parts.some(part_0 => part_0.type === 'text'),
      lastTitle: lastReasoningTitle
    };
  }, [message.parts, enabled]);
  const reasoningIsStreaming = enabled && !chainOfThoughtMeta.hasTextPart && isStreaming && isLast;

  // Snapshot progress messages per tool step so they don't leak across tool calls.
  // Only updates when progressData actually changes (not when steps change).
  const [stepProgressMap, setStepProgressMap] = useState<Record<string, string>>({});
  const lastProgressDataRef = useRef(progressData);
  useEffect(() => {
    if (!enabled) return;
    if (progressData === lastProgressDataRef.current) return;
    lastProgressDataRef.current = progressData;
    if (!progressData?.message) return;
    const {
      steps: steps_0
    } = chainOfThoughtMeta;
    const reversedEntries = steps_0.map((step_1, i_0) => ({
      step: step_1,
      i: i_0
    })).reverse();
    const active = reversedEntries.find(({
      step: step_2
    }) => step_2.type === 'tool' && getCoTStepStatus(step_2) === 'active');
    if (active && active.step.type === 'tool') {
      const key = `tool-${active.step.toolName}-${active.i}`;
      setStepProgressMap(prev => prev[key] === progressData.message ? prev : {
        ...prev,
        [key]: progressData.message!
      });
    }
  }, [enabled, progressData, chainOfThoughtMeta]);
  return {
    chainOfThoughtMeta,
    reasoningIsStreaming,
    stepProgressMap: enabled ? stepProgressMap : EMPTY_PROGRESS
  };
}